import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Pencil, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable Edit-with-Approval button (Wave C).
 *
 * Props:
 *   module     : "grn" | "quality_inspection"
 *   record     : current record object (must have id and editable fields)
 *   label      : human label shown in the modal header
 *   fields     : array of editable field configs (see EditApprovals for full list)
 *   materialsMap : optional { [material_id]: { code, name } } — when provided,
 *                  the JSON editor surfaces a small lookup card so users don't
 *                  have to remember which UUID is which material.
 *   onSubmitted: callback after request is sent
 *
 * Behaviour:
 *   - Hidden unless /api/edit-requests/can-edit returns allowed=true.
 *   - User edits values; only changed fields are sent. Diff preview is shown
 *     before submit.
 *   - Backend stores the request; Plant Head/Admin or SA approve.
 */
export default function EditRequestButton({ module, record, label, fields, materialsMap, materials, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [allowed, setAllowed] = useState(false);
  const [vals, setVals] = useState({});
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!record?.id || record?.deleted_at) { setAllowed(false); return; }
    let cancelled = false;
    api.get(`/edit-requests/can-edit?module=${module}&record_id=${record.id}`)
      .then((r) => { if (!cancelled) setAllowed(!!r.data.allowed); })
      .catch(() => { if (!cancelled) setAllowed(false); });
    return () => { cancelled = true; };
  }, [module, record?.id, record?.deleted_at]);

  useEffect(() => {
    if (!open) return;
    const initial = {};
    for (const f of fields || []) {
      const v = f.fromRecord ? f.fromRecord(record) : record[f.key];
      if (f.type === "json") {
        initial[f.key] = JSON.stringify(v ?? null, null, 2);
      } else if (f.type === "items_grn" || f.type === "items_qi") {
        // Wave-G #1: structured editors keep arrays in state (cloned to avoid mutating record).
        initial[f.key] = JSON.parse(JSON.stringify(v ?? []));
      } else {
        initial[f.key] = v ?? "";
      }
    }
    setVals(initial);
    setReason("");
  }, [open]); // eslint-disable-line

  if (!allowed) return null;

  // Build the diff payload: only fields with changed serialized values.
  const buildChanges = () => {
    const out = {};
    for (const f of fields || []) {
      const current = f.fromRecord ? f.fromRecord(record) : record[f.key];
      let proposed = vals[f.key];
      if (f.type === "number") proposed = proposed === "" ? null : Number(proposed);
      if (f.type === "json") {
        try { proposed = JSON.parse(proposed); } catch { throw new Error(`${f.label} must be valid JSON`); }
      }
      // items_grn / items_qi already store actual arrays; no JSON parse needed.
      if (f.toServer) proposed = f.toServer(proposed);
      if (JSON.stringify(current ?? null) !== JSON.stringify(proposed ?? null)) {
        out[f.key] = proposed;
      }
    }
    return out;
  };

  const changesList = (() => {
    try { return buildChanges(); } catch { return {}; }
  })();
  const changedKeys = Object.keys(changesList);

  const submit = async () => {
    if (reason.trim().length < 10) {
      toast.error("Please provide a reason (min 10 chars).");
      return;
    }
    let changes;
    try { changes = buildChanges(); } catch (e) { toast.error(e.message); return; }
    if (Object.keys(changes).length === 0) {
      toast.error("Nothing changed.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/edit-requests", { module, record_id: record.id, changes, reason });
      toast.success("Edit request sent for approval.");
      setOpen(false);
      onSubmitted?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} title="Edit (sent for approval)"
        className="text-[#1565C0] hover:bg-[#1565C0]/10 p-1 rounded-sm" data-testid={`edit-req-${module}-${record.id}`}>
        <Pencil size={14}/>
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-[#0D2B5E]/40 backdrop-blur-sm flex items-center justify-center p-8" data-testid="edit-request-modal">
          <div className="bg-white border border-[#EEEEEE] rounded-sm shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between border-b border-[#EEEEEE] pb-3 mb-4">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#1565C0]">Edit (with approval)</div>
                <div className="text-base font-bold text-[#0D2B5E] font-mono">{label || record.id}</div>
              </div>
              <button onClick={() => setOpen(false)} className="text-[#0D2B5E]/60 hover:text-[#C62828] p-1.5" data-testid="edit-request-close"><X size={18}/></button>
            </div>
            <div className="text-sm text-[#0D2B5E]/70 mb-4 leading-relaxed">
              Your edit will <strong>not</strong> change this record immediately — it goes to <strong>Plant Head / Admin</strong> for approval. The original record stays active until approved.
            </div>
            <div className="space-y-3">
              {(fields || []).map((f) => {
                // Wave-G #1: structured editors per field type. The "json" type
                // is kept as a fallback for power users / non-items fields, but
                // GRN / Quality items get a proper UI now (no more raw JSON).
                if (f.type === "items_grn") {
                  const arr = vals[f.key] || [];
                  const set = (next) => setVals((v) => ({ ...v, [f.key]: next }));
                  const update = (i, patch) => set(arr.map((row, idx) => idx === i ? { ...row, ...patch } : row));
                  return (
                    <div key={f.key} data-testid={`edit-field-${f.key}`}>
                      <label className="block text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60 mb-2">{f.label}</label>
                      <div className="border border-[#EEEEEE] rounded-sm overflow-hidden">
                        <table className="w-full text-xs">
                          <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
                            <tr>
                              <th className="text-left px-2 py-1.5">Material</th>
                              <th className="text-right px-2 py-1.5">Received Qty</th>
                              <th className="text-right px-2 py-1.5">Boxes</th>
                              <th className="text-right px-2 py-1.5">Rate</th>
                              <th className="text-left px-2 py-1.5">Box Qtys (csv)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {arr.map((row, i) => {
                              const m = materialsMap?.[row.material_id];
                              return (
                                <tr key={row.material_id || `r-${i}`} className="border-t border-[#EEEEEE]">
                                  <td className="px-2 py-1">
                                    <div className="text-[#0D2B5E] font-mono text-[11px]">{m ? `${m.code} · ${m.name}` : "(unknown)"}</div>
                                  </td>
                                  <td className="px-2 py-1">
                                    <input type="number" step="any" value={row.received_qty ?? row.accepted_qty ?? ""} onChange={(e) => update(i, { received_qty: e.target.value === "" ? null : Number(e.target.value) })}
                                      data-testid={`edit-grn-qty-${i}`}
                                      className="w-24 border border-[#EEEEEE] px-1.5 py-1 text-right font-mono text-xs rounded-sm"/>
                                  </td>
                                  <td className="px-2 py-1">
                                    <input type="number" value={row.accepted_packing_qty ?? ""} onChange={(e) => update(i, { accepted_packing_qty: e.target.value === "" ? null : Number(e.target.value) })}
                                      className="w-20 border border-[#EEEEEE] px-1.5 py-1 text-right font-mono text-xs rounded-sm"/>
                                  </td>
                                  <td className="px-2 py-1">
                                    <input type="number" step="any" value={row.rate ?? ""} onChange={(e) => update(i, { rate: e.target.value === "" ? null : Number(e.target.value) })}
                                      className="w-24 border border-[#EEEEEE] px-1.5 py-1 text-right font-mono text-xs rounded-sm"/>
                                  </td>
                                  <td className="px-2 py-1">
                                    <input value={(row.box_qtys || []).join(",")} onChange={(e) => update(i, { box_qtys: e.target.value.split(",").map((x) => x.trim()).filter(Boolean).map(Number) })}
                                      placeholder="e.g. 10,10,5"
                                      className="w-44 border border-[#EEEEEE] px-1.5 py-1 font-mono text-xs rounded-sm"/>
                                  </td>
                                </tr>
                              );
                            })}
                            {arr.length === 0 && <tr><td colSpan={5} className="p-3 text-center text-xs text-[#0D2B5E]/50">No items.</td></tr>}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                }
                if (f.type === "items_qi") {
                  const arr = vals[f.key] || [];
                  const set = (next) => setVals((v) => ({ ...v, [f.key]: next }));
                  const update = (i, patch) => set(arr.map((row, idx) => idx === i ? { ...row, ...patch } : row));
                  const updateParam = (i, pi, patch) => update(i, {
                    parameters: (arr[i].parameters || []).map((p, idx) => idx === pi ? { ...p, ...patch } : p),
                  });
                  return (
                    <div key={f.key} data-testid={`edit-field-${f.key}`} className="space-y-3">
                      <label className="block text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60">{f.label}</label>
                      {arr.map((it, i) => {
                        const m = materialsMap?.[it.material_id];
                        return (
                          <div key={it.material_id || `it-${i}`} className="border border-[#EEEEEE] rounded-sm">
                            <div className="bg-[#F5F5F5] px-3 py-1.5 flex items-center justify-between">
                              <div className="text-[11px] font-mono">
                                <span className="font-bold text-[#0D2B5E]">{m ? m.code : "(unknown)"}</span>
                                <span className="text-[#0D2B5E]/70 ml-2">{m?.name}</span>
                                <span className="text-[#0D2B5E]/40 ml-2">· Insp {it.inspection_qty || "—"} / Sample {it.sample_size || "—"}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <label className="text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold mr-1">Overall</label>
                                {["ok", "ng", "hold"].map((opt) => (
                                  <button key={opt} type="button" onClick={() => update(i, { overall_result: opt })}
                                    data-testid={`edit-qi-overall-${i}-${opt}`}
                                    className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${it.overall_result === opt ? (opt === "ok" ? "bg-[#2E7D32] text-white" : opt === "ng" ? "bg-[#C62828] text-white" : "bg-[#E65100] text-white") : "border border-[#EEEEEE] text-[#0D2B5E]/60"}`}>
                                    {opt}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div className="p-2 space-y-1">
                              {(it.parameters || []).map((p, pi) => (
                                <div key={`${p.name}-${pi}`} className="grid grid-cols-12 gap-2 items-center text-xs">
                                  <div className="col-span-3 font-mono text-[11px] text-[#0D2B5E]">{p.name}{p.mandatory && <span className="text-[#C62828]">*</span>}</div>
                                  <div className="col-span-3 text-[10px] text-[#0D2B5E]/60 truncate" title={p.specification}>{p.specification}</div>
                                  <div className="col-span-5">
                                    {Array.isArray(p.samples) && p.samples.length > 0 ? (
                                      <div className="flex flex-wrap gap-1">
                                        {p.samples.map((s, si) => (
                                          <input key={si} value={s ?? ""} onChange={(e) => {
                                            const next = [...p.samples];
                                            next[si] = e.target.value;
                                            updateParam(i, pi, { samples: next });
                                          }} data-testid={`edit-qi-sample-${i}-${pi}-${si}`}
                                            className="w-16 border border-[#EEEEEE] px-1.5 py-0.5 font-mono text-[11px] rounded-sm"/>
                                        ))}
                                      </div>
                                    ) : (
                                      <input value={p.result ?? ""} onChange={(e) => updateParam(i, pi, { result: e.target.value })}
                                        className="w-full border border-[#EEEEEE] px-1.5 py-0.5 font-mono text-[11px] rounded-sm"/>
                                    )}
                                  </div>
                                  <div className="col-span-1 text-[10px] text-[#0D2B5E]/40 text-right">{p.result_type === "numeric" ? "num" : "p/f"}</div>
                                </div>
                              ))}
                              <div className="pt-2">
                                <label className="text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold">Remarks</label>
                                <input value={it.remarks || ""} onChange={(e) => update(i, { remarks: e.target.value })}
                                  className="w-full border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm mt-0.5"/>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                }
                // Generic / json / text / number / textarea fields
                let matRef = null;
                if (f.type === "json" && materialsMap) {
                  try {
                    const parsed = JSON.parse(vals[f.key] || "null");
                    const ids = Array.isArray(parsed)
                      ? Array.from(new Set(parsed.map((x) => x && x.material_id).filter(Boolean)))
                      : [];
                    if (ids.length > 0) {
                      matRef = ids.map((id) => ({
                        id,
                        label: materialsMap[id]
                          ? `${materialsMap[id].code} · ${materialsMap[id].name}`
                          : "(unknown material)",
                      }));
                    }
                  } catch { /* invalid JSON while typing — ignore until next valid parse */ }
                }
                return (
                  <div key={f.key} data-testid={`edit-field-${f.key}`}>
                    <label className="block text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60 mb-1">{f.label}</label>
                    {matRef && matRef.length > 0 && (
                      <div className="bg-[#F5F5F5] border border-[#EEEEEE] rounded-sm p-2 mb-2 text-[11px]" data-testid={`material-ref-${f.key}`}>
                        <div className="text-[9px] uppercase tracking-widest text-[#0D2B5E]/50 font-bold mb-1">Material reference</div>
                        <ul className="space-y-0.5 font-mono">
                          {matRef.map((r) => (
                            <li key={r.id} className="flex gap-2">
                              <span className="text-[#0D2B5E]/40 text-[10px]" title={r.id}>{r.id.slice(0, 8)}…</span>
                              <span className="text-[#0D2B5E] font-bold">{r.label}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {f.type === "textarea" || f.type === "json" ? (
                      <textarea value={vals[f.key] ?? ""} onChange={(e) => setVals((v) => ({ ...v, [f.key]: e.target.value }))}
                        rows={f.type === "json" ? 8 : 3}
                        className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm font-mono"/>
                    ) : (
                      <input type={f.type || "text"} value={vals[f.key] ?? ""} onChange={(e) => setVals((v) => ({ ...v, [f.key]: e.target.value }))}
                        className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm"/>
                    )}
                  </div>
                );
              })}
            </div>
            {changedKeys.length > 0 && (
              <div className="mt-5 border border-[#FFB74D]/40 bg-[#FFF3E0] rounded-sm p-3" data-testid="edit-diff-preview">
                <div className="text-[10px] tracking-widest uppercase font-bold text-[#E65100] mb-2">Changes preview ({changedKeys.length})</div>
                <div className="space-y-1 text-xs font-mono text-[#0D2B5E]/80 max-h-40 overflow-y-auto">
                  {changedKeys.map((k) => {
                    const f = (fields || []).find((x) => x.key === k);
                    const old = f?.fromRecord ? f.fromRecord(record) : record[k];
                    const nv = changesList[k];
                    const fmt = (v) => f?.render ? f.render(v) : (typeof v === "object" ? JSON.stringify(v) : String(v ?? "—"));
                    return (
                      <div key={k} className="border-b border-[#FFB74D]/30 pb-1">
                        <span className="font-bold text-[#0D2B5E]">{f?.label || k}</span>
                        <span className="ml-2 text-[#C62828] line-through" title="Old">{fmt(old)}</span>
                        <span className="mx-1 text-[#0D2B5E]/40">→</span>
                        <span className="text-[#2E7D32]" title="New">{fmt(nv)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <label className="block text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60 mt-4 mb-1">Reason (min 10 chars, mandatory)</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500}
              className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm"
              placeholder="Why is this edit needed?" data-testid="edit-reason-input"/>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setOpen(false)} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
              <button onClick={submit} disabled={busy || reason.trim().length < 10 || changedKeys.length === 0}
                className="px-5 py-2 bg-[#1565C0] hover:bg-[#0D47A1] disabled:bg-[#BBB] text-white text-sm rounded-sm font-bold uppercase tracking-wider"
                data-testid="edit-request-submit">
                {busy ? "Sending…" : "Send for approval"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
